import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { PageableFilterRequest } from '../../lib/models/fundamental/types';
import {
  getDividendPayers as getDividendPayersCompanies,
  getHighGrowthCompanies as getGrowthCompanies,
} from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { RootState } from '../../lib/store/index';
import { formatCurrency, getLimitedText, roundPercentValues } from '../../lib/util/DataHelpers';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import { ClickableDiv, CompanyLogo, DiscoverInternalTable, GoBackButton } from '../../components';
import { useIsMobile } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';

import SkeletonLoaderRow from './SkeletonLoaderRow';

import styles from './DividendPayersAndHighGrowthCompanies.module.scss';

const DividendPayersAndHighGrowthCompaniesContainer = (props: ReduxProps) => {
  const {
    dividendPayers,
    highGrowthCompanies,
    getDividendPayers,
    getHighGrowthCompanies,
    companyLogoAndName,
    isDividendPayersStatusPending,
    isHighGrowthCompaniesStatusPending,
  } = props;

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as { currentTab: number | null; currentTabIndex?: number };
  const { currentTab, currentTabIndex } = locationState['navigation-state'];

  const [activeTabIndex, setActiveTabIndex] = useState<number>(currentTab || 0);
  const [currentDividendPage, setCurrentDividendPage] = useState<number>(0);
  const [currentGrowthPage, setCurrentGrowthPage] = useState<number>(0);

  const nameLimit = isMobile ? 14 : 18;
  const isReduction = activeTabIndex === 1;

  useEffect(() => {
    if (currentTab != null) {
      if (!dividendPayers.content.length && !currentTab) {
        getDividendPayers({ page: 0 });
      }
      if (!highGrowthCompanies.content.length && currentTab) {
        getHighGrowthCompanies({ page: 0 });
      }
    }
  }, [getDividendPayers, getHighGrowthCompanies, dividendPayers, highGrowthCompanies, locationState, currentTab]);

  useEffect(() => {
    if (currentTab == null && (!dividendPayers.content.length || !highGrowthCompanies.content.length)) {
      getDividendPayers({ page: 0 });
      getHighGrowthCompanies({ page: 0 });
    }
  }, [dividendPayers, highGrowthCompanies, locationState, getDividendPayers, getHighGrowthCompanies, currentTab]);

  const onViewMoreClick = () => {
    if (activeTabIndex === 0) {
      getDividendPayers({ page: currentDividendPage + 1 });
      setCurrentDividendPage(currentDividendPage + 1);
    } else {
      getHighGrowthCompanies({ page: currentGrowthPage + 1 });
      setCurrentGrowthPage(currentGrowthPage + 1);
    }
  };

  const onTabChange = (index: number) => {
    setActiveTabIndex(index);
  };

  const renderList = (activeTabIndex === 0 ? dividendPayers.content : highGrowthCompanies.content).length === 0 ? (
    <>
      <SkeletonLoaderRow />
      <SkeletonLoaderRow />
      <SkeletonLoaderRow />
      <SkeletonLoaderRow />
    </>
  ) : (
    (activeTabIndex === 0 ? dividendPayers.content : highGrowthCompanies.content).map((item) => {
      const logo = companyLogoAndName[item.symbol]?.logo;
      const url = companyLogoAndName[item.symbol]?.url ?? item?.url;
      const displayName = getLimitedText(item.name, nameLimit);
      if (isMobile) {
        return (
          <ClickableDiv
            key={item.symbol}
            onClick={() => navigate(`${UI_ROUTES.symbol}/${item.symbol}`)}
            className={styles.wrapperCompanyMob}
          >
            <div className={styles.left}>
              <div className={styles.logoWrapper}>
                {(url || logo)
                && <CompanyLogo url={url} logo={logo} symbol={item.symbol} />}
              </div>
              <div className={styles.companyInfo}>
                <span className={styles.symbol}>{item.symbol}</span>
                <div className={styles.displayName}>{displayName}</div>
              </div>
            </div>
            <div className={styles.right}>
              {!isReduction && `${roundPercentValues(item.dividendYield * 100)}%`}
              {isReduction && `${item.growthIndicator}%`}
            </div>
          </ClickableDiv>
        );
      }

      return (
        <tr key={item.symbol} onClick={() => navigate(`${UI_ROUTES.symbol}/${item.symbol}`)}>
          <td className={styles.companyTab}>
            <div className={styles.wrapperCompany}>
              {(url || logo) && <CompanyLogo url={url} logo={logo} symbol={item.symbol} />}
              <div className={styles.companyInfo}>
                {!item.symbol || !displayName ? (
                  <>
                    <div className={classNames(styles.loadingComponent, styles.loadingComponentMargin)} />
                    <div className={styles.loadingComponent} style={{ width: 134 }} />
                  </>
                ) : (
                  <div className={styles.nameAndSymbol}>
                    <div className={styles.symbol}>{item.symbol}</div>
                    <div className={styles.name}>{displayName}</div>
                  </div>
                )}
              </div>
            </div>
          </td>
          <td className={styles.tableComponent}>
            {!isReduction && `${roundPercentValues(item.dividendYield * 100)}%`}
            {isReduction && `${item.growthIndicator}%`}
          </td>
          <td className={styles.tableComponent}>
            {formatCurrency(isReduction ? item.revenue : item.dividendRate, false, true)}
          </td>
          <td className={styles.tableComponent}>
            {formatCurrency(isReduction ? item.epsLast12Months : item.marketCap, false, true)}
          </td>
          <td className={styles.tableComponent}>
            {formatCurrency(isReduction ? item.marketCap : item.volume, false, true)}
          </td>
        </tr>
      );
    })
  );

  return (
    <>
      <div
        className={
          isMobile // eslint-disable-line no-nested-ternary
            ? classNames(styles.wrapperMob, styles.noMargin)
            : styles.wrapper
        }
      >
        {!isMobile && (
          <div className={styles.goBackButton}>
            <GoBackButton />
          </div>
        )}
        <h6 className={styles.title}>{lang.commonDiscoverSubtitleLongTermInvestments()}</h6>
        <div className={styles.table}>
          <DiscoverInternalTable
            hasViewMoreButton={
              isReduction
                ? highGrowthCompanies.totalPages > currentGrowthPage + 1
                : dividendPayers.totalPages > currentDividendPage + 1
            }
            onViewMoreClick={onViewMoreClick}
            isViewMoreDisabled={isDividendPayersStatusPending || isHighGrowthCompaniesStatusPending}
            fullWidthButtons
            hasSecondTable
            onTabChange={onTabChange}
            tableNames={[lang.commonDiscoverDividendPayers(), lang.commonDiscoverHighGrowthCompanies()]}
            tableHeaders={
              isReduction
                ? [
                  lang.headerAccountBreakdownSymbolName(),
                  lang.commonDiscoverHighGrowthCompaniesLegend(),
                  lang.commonDiscoverInfoBoxRevenue(),
                  lang.commonDiscoverInfoBoxEps(),
                  lang.commonTopGainersAndLosersMarketCap(),
                ]
                : [
                  lang.headerAccountBreakdownSymbolName(),
                  lang.commonDiscoverDividendPayersLegend(),
                  lang.commonDiscoverInfoBoxDividendRate(),
                  lang.commonTopGainersAndLosersMarketCap(),
                  lang.commonDiscoverInfoBoxVolume(),
                ]
            }
            renderList={renderList}
            initialActiveTab={activeTabIndex}
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  dividendPayers: state.fundamental.dividendPayers,
  highGrowthCompanies: state.fundamental.highGrowthCompanies,
  companyLogoAndName: state.fundamental.companyLogoAndName,
  isDividendPayersStatusPending:
    isCallStatusPending(FundamentalCall.getDividendPayers, state.fundamental.statusByCall),
  isHighGrowthCompaniesStatusPending:
    isCallStatusPending(FundamentalCall.getHighGrowthCompanies, state.fundamental.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getDividendPayers: (payload: PageableFilterRequest) => dispatch(getDividendPayersCompanies(payload)),
  getHighGrowthCompanies: (payload: PageableFilterRequest) => dispatch(getGrowthCompanies(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(DividendPayersAndHighGrowthCompaniesContainer);
